"use client";

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { RotateCcw, Play, Pause } from 'lucide-react';
import { TESLA_REQUIREMENTS } from '@/lib/audio-utils';
import { fmtDurationPrecise } from '@/lib/tls-format';
import { playToggleA11y } from '@/lib/a11y/play-toggle';
import { triggerHaptic } from '@/lib/haptics';

interface AudioTrimmerProps {
  /** Total duration of the audio in seconds */
  duration: number;
  /** Pre-computed waveform data (0-100 values) */
  waveformData?: number[];
  /** Current playback time in seconds */
  currentTime?: number;
  /** Whether audio is currently playing */
  isPlaying?: boolean;
  /** Callback when trim range changes */
  onTrimChange: (startTime: number, endTime: number) => void;
  /** Callback to seek audio playback */
  onSeek?: (time: number) => void;
  /** Callback to toggle play/pause */
  onPlayPause?: () => void;
  /** Whether trim preview mode is active */
  trimModeActive?: boolean;
  /** Callback when trim mode changes */
  onTrimModeChange?: (active: boolean) => void;
  /** Whether loop is enabled in trim mode */
  loopEnabled?: boolean;
  /** Callback when loop setting changes */
  onLoopChange?: (enabled: boolean) => void;
  /** Additional CSS classes */
  className?: string;
  /** Number of bars to display */
  barCount?: number;
}

interface TrimWaveformBar {
  id: string;
  value: number;
}

/**
 * Audio trimmer component with draggable start/end handles
 * Used for sounds longer than Tesla's 5-second limit
 */
// eslint-disable-next-line react-doctor/no-giant-component -- audio trimmer integrates waveform visualization, drag handles, and keyboard controls; decomposing would split tightly-coupled UI state
export function AudioTrimmer({
  duration,
  waveformData,
  currentTime = 0,
  isPlaying = false,
  onTrimChange,
  onSeek,
  onPlayPause,
  trimModeActive = false,
  onTrimModeChange,
  loopEnabled = true,
  onLoopChange,
  className,
  barCount = 48,
}: AudioTrimmerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState<'start' | 'end' | null>(null);
  const prevDurationRef = useRef(duration);
  // §12.8: fire the trim-bound haptic once per bound-hit, not on every
  // mousemove while a handle is pinned at the bound — tracked per handle so
  // dragging off and back onto the bound fires again.
  const startAtBoundRef = useRef(false);
  const endAtBoundRef = useRef(false);
  // eslint-disable-next-line react-doctor/no-prop-callback-in-effect -- stable ref avoids stale closure without changing external API
  const onTrimChangeRef = useRef(onTrimChange);
  useEffect(() => { onTrimChangeRef.current = onTrimChange; }, [onTrimChange]);

  // Initialize trim to first 5 seconds (Tesla max)
  const maxTrimDuration = TESLA_REQUIREMENTS.MAX_DURATION;
  const [trimStart, setTrimStart] = useState(0);
  const [trimEnd, setTrimEnd] = useState(() => Math.min(maxTrimDuration, duration));

  // Track duration changes and notify parent via callback (not state update in effect)
  // eslint-disable-next-line react-doctor/no-pass-live-state-to-parent -- notifying parent of external duration change; restructure would require lifting state
  useEffect(() => {
    if (prevDurationRef.current !== duration && trimEnd > duration) {
      // Duration changed and trimEnd exceeds it - notify parent
      onTrimChangeRef.current(trimStart, duration);
    }
    prevDurationRef.current = duration;
  }, [duration, trimEnd, trimStart]);

  // Derive effective trim end (clamped to duration)
  const effectiveTrimEnd = Math.min(trimEnd, duration);

  // Generate bars from waveform data or create default
  const bars = React.useMemo<TrimWaveformBar[]>(() => {
    const createBar = (value: number, index: number, sourceIndex?: number): TrimWaveformBar => ({
      id: `trim-waveform-bar-${barCount}-${index}${sourceIndex === undefined ? '' : `-src-${sourceIndex}`}`,
      value,
    });

    if (waveformData && waveformData.length > 0) {
      // Resample to barCount
      const resampled: TrimWaveformBar[] = [];
      for (let i = 0; i < barCount; i++) {
        const srcIdx = Math.floor((i / barCount) * waveformData.length);
        resampled.push(createBar(waveformData[srcIdx] || 50, i, srcIdx));
      }
      return resampled;
    }
    // Default deterministic wave pattern (no random for purity)
    return Array.from({ length: barCount }, (_, i) =>
      createBar(30 + Math.sin(i * 0.3) * 20 + Math.sin(i * 0.7 + 1.5) * 15, i)
    );
  }, [waveformData, barCount]);

  // Convert pixel position to time
  const positionToTime = useCallback((clientX: number): number => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    const ratio = Math.max(0, Math.min(1, x / rect.width));
    return ratio * duration;
  }, [duration]);

  // Handle drag start
  const handleDragStart = useCallback((handle: 'start' | 'end') => (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    setIsDragging(handle);
  }, []);

  // Handle drag move
  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDragging) return;

    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const time = positionToTime(clientX);

    if (isDragging === 'start') {
      // Ensure start doesn't exceed end minus minimum duration
      const newStart = Math.max(0, Math.min(time, trimEnd - 0.5));
      // Ensure trim duration doesn't exceed max
      const effectiveStart = Math.max(newStart, trimEnd - maxTrimDuration);
      // §12.8: "trim handle hitting a bound" — the file's absolute start.
      const atBound = effectiveStart <= 0;
      if (atBound && !startAtBoundRef.current) triggerHaptic('trim-bound');
      startAtBoundRef.current = atBound;
      setTrimStart(effectiveStart);
      onTrimChange(effectiveStart, trimEnd);
    } else {
      // Ensure end doesn't go below start plus minimum duration
      const newEnd = Math.max(trimStart + 0.5, Math.min(time, duration));
      // Ensure trim duration doesn't exceed max
      const effectiveEnd = Math.min(newEnd, trimStart + maxTrimDuration);
      // §12.8: "trim handle hitting a bound" — the file's absolute end.
      const atBound = effectiveEnd >= duration;
      if (atBound && !endAtBoundRef.current) triggerHaptic('trim-bound');
      endAtBoundRef.current = atBound;
      setTrimEnd(effectiveEnd);
      onTrimChange(trimStart, effectiveEnd);
    }
  }, [isDragging, positionToTime, trimStart, trimEnd, duration, maxTrimDuration, onTrimChange]);

  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setIsDragging(null);
    startAtBoundRef.current = false;
    endAtBoundRef.current = false;
  }, []);

  // Add/remove event listeners
  // eslint-disable-next-line react-doctor/advanced-event-handler-refs -- handleDragMove/handleDragEnd are stable useCallbacks; no stale closure risk
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mousemove', handleDragMove);
      window.addEventListener('mouseup', handleDragEnd);
      window.addEventListener('touchmove', handleDragMove, { passive: true });
      window.addEventListener('touchend', handleDragEnd, { passive: true });
      return () => {
        window.removeEventListener('mousemove', handleDragMove);
        window.removeEventListener('mouseup', handleDragEnd);
        window.removeEventListener('touchmove', handleDragMove);
        window.removeEventListener('touchend', handleDragEnd);
      };
    }
  }, [isDragging, handleDragMove, handleDragEnd]);

  // Handle click on waveform to seek
  const handleWaveformClick = useCallback((e: React.MouseEvent) => {
    if (isDragging || !onSeek) return;
    const time = positionToTime(e.clientX);
    // Only seek within trimmed region
    if (time >= trimStart && time <= effectiveTrimEnd) {
      onSeek(time);
    }
  }, [isDragging, onSeek, positionToTime, trimStart, effectiveTrimEnd]);

  const handleWaveformKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!onSeek) return;

    const step = e.shiftKey ? 1 : 0.1;
    const clampedCurrent = Math.min(Math.max(currentTime, trimStart), effectiveTrimEnd);
    let nextTime: number | null = null;

    if (e.key === 'ArrowLeft') {
      nextTime = Math.max(trimStart, clampedCurrent - step);
    } else if (e.key === 'ArrowRight') {
      nextTime = Math.min(effectiveTrimEnd, clampedCurrent + step);
    } else if (e.key === 'Home') {
      nextTime = trimStart;
    } else if (e.key === 'End') {
      nextTime = effectiveTrimEnd;
    }

    if (nextTime !== null) {
      e.preventDefault();
      onSeek(nextTime);
    }
  }, [currentTime, effectiveTrimEnd, onSeek, trimStart]);

  // Reset to first 5 seconds
  const handleReset = useCallback(() => {
    setTrimStart(0);
    setTrimEnd(Math.min(maxTrimDuration, duration));
    onTrimChange(0, Math.min(maxTrimDuration, duration));
  }, [duration, maxTrimDuration, onTrimChange]);

  const moveStartHandle = useCallback((delta: number) => {
    const newStart = Math.max(0, Math.min(trimStart + delta, trimEnd - 0.5));
    const effectiveStart = Math.max(newStart, trimEnd - maxTrimDuration);
    setTrimStart(effectiveStart);
    onTrimChange(effectiveStart, trimEnd);
  }, [maxTrimDuration, onTrimChange, trimEnd, trimStart]);

  const moveEndHandle = useCallback((delta: number) => {
    const newEnd = Math.max(trimStart + 0.5, Math.min(trimEnd + delta, duration));
    const effectiveEnd = Math.min(newEnd, trimStart + maxTrimDuration);
    setTrimEnd(effectiveEnd);
    onTrimChange(trimStart, effectiveEnd);
  }, [duration, maxTrimDuration, onTrimChange, trimEnd, trimStart]);

  const handleHandleKeyDown = useCallback(
    (handle: 'start' | 'end') => (e: React.KeyboardEvent<HTMLDivElement>) => {
      const step = e.shiftKey ? 0.5 : 0.1;

      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (handle === 'start') moveStartHandle(-step);
        else moveEndHandle(-step);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (handle === 'start') moveStartHandle(step);
        else moveEndHandle(step);
      } else if (e.key === 'Home') {
        e.preventDefault();
        if (handle === 'start') {
          setTrimStart(0);
          onTrimChange(0, trimEnd);
        } else {
          const nextEnd = Math.min(trimStart + 0.5, duration);
          setTrimEnd(nextEnd);
          onTrimChange(trimStart, nextEnd);
        }
      } else if (e.key === 'End') {
        e.preventDefault();
        if (handle === 'start') {
          const nextStart = Math.max(0, effectiveTrimEnd - maxTrimDuration);
          setTrimStart(nextStart);
          onTrimChange(nextStart, trimEnd);
        } else {
          const nextEnd = Math.min(duration, trimStart + maxTrimDuration);
          setTrimEnd(nextEnd);
          onTrimChange(trimStart, nextEnd);
        }
      }
    },
    [duration, effectiveTrimEnd, maxTrimDuration, moveEndHandle, moveStartHandle, onTrimChange, trimEnd, trimStart]
  );

  // Calculate positions (using effectiveTrimEnd for display)
  const startPercent = (trimStart / duration) * 100;
  const endPercent = (effectiveTrimEnd / duration) * 100;
  const currentPercent = (currentTime / duration) * 100;
  const trimDuration = effectiveTrimEnd - trimStart;

  // Format time as m:ss.SS (§01.9 precision form — never "2s")
  const formatTime = fmtDurationPrecise;

  // Handle entering/exiting trim mode
  // Note: We don't auto-play here - the parent component handles that
  // after the state update is complete
  const handleEnterTrimMode = useCallback(() => {
    onTrimModeChange?.(true);
  }, [onTrimModeChange]);

  const handleExitTrimMode = useCallback(() => {
    onTrimModeChange?.(false);
  }, [onTrimModeChange]);

  const chromeButtonClass =
    "flex items-center gap-1.5 whitespace-nowrap rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] px-2.5 py-1.5 text-sm text-[var(--tls-fg-muted)] transition-[border-color,background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)] hover:border-[color:var(--tls-border-strong)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]";
  const primaryButtonClass =
    "flex items-center gap-1.5 whitespace-nowrap rounded-[var(--ls-radius-0)] bg-[var(--tls-brand)] px-3 py-1.5 text-sm font-medium text-[var(--tls-brand-fg)] transition-[background-color,color] duration-[var(--motion-duration-standard)] ease-[var(--motion-ease-standard)] hover:bg-[var(--tls-brand-hover)]";
  // Selection is one of the accent's six roles; the trim handles and the
  // preview-mode chrome are selection UI, so they carry the accent, not a
  // semantic amber. --tls-brand-ink is the AA-safe accent (raw --tls-brand
  // measures ~2.97:1 on the dark canvas).
  const selectionTintStyle: React.CSSProperties = {
    borderColor: "color-mix(in srgb, var(--tls-brand-ink) 60%, transparent)",
    background: "var(--tls-brand-soft)",
    color: "var(--tls-fg-strong)",
  };

  return (
    <div className={cn("space-y-3", className)}>
      {/* Trim mode indicator banner */}
      {trimModeActive && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] px-3 py-2">
          <div className="flex flex-wrap items-center gap-x-2">
            <div className="size-2 rounded-[var(--radius-pill)] bg-[var(--tls-brand-ink)]" />
            <span className="text-sm font-medium text-[var(--tls-fg-strong)]">
              Preview mode
            </span>
            <span className="text-sm text-[var(--tls-fg-muted)]">
              Playing selection only
            </span>
          </div>
          <div className="flex items-center gap-2">
            {/* Loop toggle */}
            <button type="button"
              onClick={() => onLoopChange?.(!loopEnabled)}
              className={cn(
                chromeButtonClass,
                loopEnabled && "hover:text-[var(--tls-fg-strong)]"
              )}
              style={loopEnabled ? selectionTintStyle : undefined}
              title={loopEnabled ? "Loop enabled" : "Loop disabled"}
            >
              {loopEnabled ? "Loop on" : "Loop off"}
            </button>
            {/* Exit trim mode */}
            <button type="button"
              onClick={handleExitTrimMode}
              className={chromeButtonClass}
            >
              Exit preview
            </button>
          </div>
        </div>
      )}

      {/* Header with trim info */}
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="flex items-center gap-2 text-[var(--tls-fg-muted)]">
          <span>
            Trim selection: <span className="font-medium text-[var(--tls-fg-strong)]">{fmtDurationPrecise(trimDuration)}</span>
            {trimDuration > maxTrimDuration && (
              <span className="ml-2 font-semibold text-[var(--tls-fg-muted)]">(max {maxTrimDuration}s)</span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Preview selection button (only show when not in trim mode) */}
          {!trimModeActive && onTrimModeChange && (
            <button type="button"
              onClick={handleEnterTrimMode}
              className={primaryButtonClass}
            >
              <Play className="size-3" />
              Preview selection
            </button>
          )}
          <button type="button"
            onClick={handleReset}
            className={chromeButtonClass}
          >
            <RotateCcw className="size-3" />
            Reset
          </button>
        </div>
      </div>

      {/*
        Waveform with trim handles.

        The three sliders here (seek, trim start, trim end) are SIBLINGS, never
        nested. ARIA gives `slider` a presentational-children model, so anything
        focusable inside a slider may be dropped from the accessibility tree
        entirely; axe flags it as `nested-interactive` and it fails WCAG 4.1.2
        (Name, Role, Value, level A). This wrapper is a plain presentational box
        that owns the border, the clipping and the `cqw` container context; the
        seek slider is an inset layer holding only decorative children, and the
        two handles are overlaid on top of it. See TES-1572.
      */}
      <div className="relative h-20 select-none overflow-hidden rounded-[var(--ls-radius-0)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface-elevated)] [container-type:inline-size]">
        {/* Seek slider: pointer + keyboard seeking. Presentational children only. */}
        <div
          ref={containerRef}
          className="tlsr-focus-inset absolute inset-0 cursor-pointer"
          onClick={handleWaveformClick}
          onKeyDown={handleWaveformKeyDown}
          // eslint-disable-next-line react-doctor/prefer-tag-over-role -- custom pointer-event waveform slider; <input type=range> can't contain child visualization bars
          role="slider"
          tabIndex={0}
          aria-label="Seek within trim selection"
          aria-valuemin={trimStart}
          aria-valuemax={effectiveTrimEnd}
          aria-valuenow={Math.min(Math.max(currentTime, trimStart), effectiveTrimEnd)}
          aria-valuetext={`${formatTime(Math.min(Math.max(currentTime, trimStart), effectiveTrimEnd))} of ${formatTime(duration)}`}
        >
        {/* Dimmed regions outside trim */}
        <div
          className="absolute inset-y-0 left-0 z-10 w-full origin-left pointer-events-none bg-[color-mix(in_srgb,var(--tls-canvas)_72%,transparent)]"
          style={{ transform: `scaleX(${startPercent / 100})` }}
        />
        <div
          className="absolute inset-y-0 left-0 z-10 w-full origin-right pointer-events-none bg-[color-mix(in_srgb,var(--tls-canvas)_72%,transparent)]"
          style={{ transform: `scaleX(${(100 - endPercent) / 100})` }}
        />

        {/* Waveform bars - no horizontal padding so they align with handles */}
        <div className="absolute inset-0 flex items-end gap-[1px] py-2">
          {bars.map(({ id, value }, index) => {
            const barPosition = (index / bars.length) * 100;
            const isInTrim = barPosition >= startPercent && barPosition <= endPercent;
            const isPlayed = currentPercent > barPosition;

            return (
              <div
                key={id}
                className={cn(
                  "flex-1 min-w-[2px] rounded-[var(--radius-pill)] transition-colors duration-[var(--motion-duration-fast)]",
                  !isInTrim && "bg-[color-mix(in_srgb,var(--tls-fg-subtle)_16%,transparent)]",
                  isInTrim && !isPlayed && "bg-[color-mix(in_srgb,var(--tls-fg-subtle)_34%,transparent)]",
                  isInTrim && isPlayed && "bg-[var(--tls-brand)]"
                )}
                style={{ height: `${value}%` }}
              />
            );
          })}
        </div>

        {/* Current playback position */}
        {currentTime >= trimStart && currentTime <= effectiveTrimEnd && (
          <div
            className="absolute left-0 top-0 bottom-0 z-20 w-0.5 bg-[var(--tls-fg-strong)] pointer-events-none"
            style={{ transform: `translate3d(${currentPercent}cqw, 0, 0)` }}
          />
        )}

        {/* Trim region border */}
        <div
          className="pointer-events-none absolute left-0 top-0 bottom-0 z-20 w-full origin-left border-t-2 border-b-2 border-[color:color-mix(in_srgb,var(--tls-brand-ink)_70%,transparent)]"
          style={{
            transform: `translate3d(${startPercent}cqw, 0, 0) scaleX(${(endPercent - startPercent) / 100})`,
          }}
        />
        </div>

        {/* Start handle: sibling of the seek slider (TES-1572 — never nested
            inside it), overlaid on top. The interactive role=slider element is
            the full 44px hit target (WCAG 2.5.8), centred on the boundary via
            translateX(-50%); the line, grip and label are decorative children. */}
        <div
          className="group tlsr-focus-strong absolute top-0 bottom-0 z-30 flex w-11 cursor-ew-resize items-center justify-center touch-none"
          style={{ transform: `translate3d(${startPercent}cqw, 0, 0) translateX(-50%)` }}
          onMouseDown={handleDragStart('start')}
          onTouchStart={handleDragStart('start')}
          onKeyDown={handleHandleKeyDown('start')}
          // eslint-disable-next-line react-doctor/prefer-tag-over-role -- custom drag handle with child grip UI; <input type=range> cannot contain children
          role="slider"
          tabIndex={0}
          aria-label="Trim start"
          aria-valuemin={0}
          aria-valuemax={effectiveTrimEnd - 0.5}
          aria-valuenow={trimStart}
          aria-valuetext={formatTime(trimStart)}
        >
          {/* Thin visual line at the exact boundary */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-[var(--tls-brand-ink)]" />
          {/* Handle grip */}
          <div aria-hidden="true" className="pointer-events-none flex h-8 w-5 items-center justify-center">
            <div
              className={cn(
                "flex h-6 w-3 items-center justify-center rounded-[var(--radius-pill)] bg-[var(--tls-brand-ink)] transition-[scale] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] group-focus-visible:scale-x-[1.15]",
                isDragging === 'start' && "scale-x-[1.15]"
              )}
            >
              <div className="h-3 w-0.5 rounded-[var(--radius-pill)] bg-[color-mix(in_srgb,var(--tls-canvas)_72%,var(--tls-brand-ink))]" />
            </div>
          </div>
          {/* Time label */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-6 whitespace-nowrap rounded-[var(--ls-radius-0)] bg-[var(--tls-brand-ink)] px-1.5 py-0.5 font-sans text-[10px] font-semibold tabular-nums text-[var(--tls-canvas)]">
            {formatTime(trimStart)}
          </div>
        </div>

        {/* End handle: sibling of the seek slider — same 44px hit-target and
            TES-1572 sibling structure as the start handle above. */}
        <div
          className="group tlsr-focus-strong absolute top-0 bottom-0 z-30 flex w-11 cursor-ew-resize items-center justify-center touch-none"
          style={{ transform: `translate3d(${endPercent}cqw, 0, 0) translateX(-50%)` }}
          onMouseDown={handleDragStart('end')}
          onTouchStart={handleDragStart('end')}
          onKeyDown={handleHandleKeyDown('end')}
          // eslint-disable-next-line react-doctor/prefer-tag-over-role -- custom drag handle with child grip UI; <input type=range> cannot contain children
          role="slider"
          tabIndex={0}
          aria-label="Trim end"
          aria-valuemin={trimStart + 0.5}
          aria-valuemax={duration}
          aria-valuenow={effectiveTrimEnd}
          aria-valuetext={formatTime(effectiveTrimEnd)}
        >
          {/* Thin visual line at the exact boundary */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-1/2 w-0.5 -translate-x-1/2 bg-[var(--tls-brand-ink)]" />
          {/* Handle grip */}
          <div aria-hidden="true" className="pointer-events-none flex h-8 w-5 items-center justify-center">
            <div
              className={cn(
                "flex h-6 w-3 items-center justify-center rounded-[var(--radius-pill)] bg-[var(--tls-brand-ink)] transition-[scale] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] group-focus-visible:scale-x-[1.15]",
                isDragging === 'end' && "scale-x-[1.15]"
              )}
            >
              <div className="h-3 w-0.5 rounded-[var(--radius-pill)] bg-[color-mix(in_srgb,var(--tls-canvas)_72%,var(--tls-brand-ink))]" />
            </div>
          </div>
          {/* Time label */}
          <div aria-hidden="true" className="pointer-events-none absolute -top-6 whitespace-nowrap rounded-[var(--ls-radius-0)] bg-[var(--tls-brand-ink)] px-1.5 py-0.5 font-sans text-[10px] font-semibold tabular-nums text-[var(--tls-canvas)]">
            {formatTime(effectiveTrimEnd)}
          </div>
        </div>
      </div>

      {/* Trim mode playback controls */}
      {trimModeActive && onPlayPause && (
        <div className="flex items-center justify-center gap-3 py-2">
          <button type="button"
            onClick={onPlayPause}
            {...playToggleA11y({ playing: isPlaying, title: 'trim preview' })}
            className={cn(
              "flex size-10 items-center justify-center rounded-[var(--radius-pill)] transition-[background-color,scale] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)]",
              "bg-[var(--tls-brand)] text-[var(--tls-brand-fg)] hover:bg-[var(--tls-brand-hover)]",
              // §12.3: scale press cue is motion-safe: gated; reuse this
              // button's own hover:bg-brand-hover target as the
              // reduced-motion confirmation.
              "motion-safe:active:scale-[0.97] motion-reduce:active:bg-[var(--tls-brand-hover)]"
            )}
          >
            {isPlaying ? (
              <Pause className="size-5" />
            ) : (
              <Play className="size-5 ml-0.5" />
            )}
          </button>
          <div className="text-sm text-[var(--tls-fg-muted)]">
            <span className="font-sans font-semibold tabular-nums text-[var(--tls-fg-strong)]">
              {formatTime(
                currentTime >= trimStart && currentTime <= effectiveTrimEnd
                  ? currentTime - trimStart
                  : 0
              )}
            </span>
            <span className="mx-1">/</span>
            <span className="font-sans font-semibold tabular-nums">{formatTime(trimDuration)}</span>
          </div>
        </div>
      )}

      {/* Duration info */}
      <div className="flex items-center justify-between text-sm text-[var(--tls-fg-subtle)]">
        <span>0:00</span>
        <span className="text-[var(--tls-fg-muted)]">
          {/* Verdicts are ink, weight carries them: affirmative 700 strong,
              negative 600 muted (the system has no semantic amber). */}
          {trimDuration <= maxTrimDuration ? (
            <span className="font-bold text-[var(--tls-fg-strong)]">Tesla-ready ({fmtDurationPrecise(trimDuration)})</span>
          ) : (
            <span className="font-semibold text-[var(--tls-fg-muted)]">Trim to {maxTrimDuration}s or less</span>
          )}
        </span>
        <span>{formatTime(duration)}</span>
      </div>
    </div>
  );
}

export default AudioTrimmer;
